import React from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { Context } from "../App";
import ModelEditor from "../components/forms/Editor";
import { getDesignById, updateDesign, uploadDesignPreview } from "../services/designService";
import { captureDesignToBlob } from "../utils/captureDesign";

export default function EditDesign() {
    const { id } = useParams();
    const { deconnect } = React.useContext(Context);

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [design, setDesign] = React.useState(null);

    React.useEffect(() => {
        let active = true;

        const run = async () => {
            try {
                const data = await getDesignById(id);
                if (!active) return;
                setDesign(data);
            } catch (e) {
                if (!active) return;
                if (e.message === "SESSION_EXPIRED") {
                    alert("Votre session a expiré. Veuillez vous reconnecter.");
                    deconnect();
                    return;
                }
                setError("Erreur lors du chargement du design.");
            } finally {
                if (active) setLoading(false);
            }
        };

        run();
        return () => {
            active = false;
        };
    }, [id, deconnect]);

    const handleSave = async (data) => {
        try {
            await updateDesign(
                id,
                { ...data, description: design?.description || "" },
                design?.model?._id || design?.model || null
            );

            // Fire-and-forget: capture and upload the preview image
            captureDesignToBlob({
                elements: data.elements,
                canvasBg: data.canvasBg,
                canvasSize: data.canvasSize,
            })
                .then((blob) => blob && uploadDesignPreview(id, blob))
                .catch((err) => console.warn("[preview] Capture failed:", err.message));

            alert("Design mis à jour.");
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            alert("Erreur lors de la mise à jour du design.");
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !design) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || "Design introuvable."}</Alert>
            </Box>
        );
    }

    return (
        <ModelEditor
            mode="design"
            initialTitle={design.title}
            initialElements={design.elements}
            initialCanvasBg={design.canvasBg}
            initialCanvasSize={design.canvasSize}
            initialDuration={design.duration ?? ""}
            initialPublishTo={design.publishTo || []}
            initialStartDate={design.startDate || ""}
            initialEndDate={design.endDate || ""}
            onSave={handleSave}
        />
    );
}
